import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './Login.css';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { API_BASE } from '../../config';
import logo from '../../media/icons/tiger1.png';
import { tr } from '../../i18n';

async function loginUser(credentials) {
  return fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: credentials.email, password: credentials.password })
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Response not OK.', response);
      }
      return response.json();
    })
    .catch((e) => {
      Swal.fire({
        title: tr("Error!"),
        text: tr("Connection problem."),
        icon: 'error',
        confirmButtonText: tr("Close")
      });
      credentials.setLoading(false);
    });
}

export default function Login({ setToken }) {
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [errorState, setErrorState] = useState({ type: false, label: tr("Password"), helperText: '' });
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      Swal.fire({
        title: tr("Error!"),
        text: tr("Email and/or password cannot be empty!"),
        icon: 'error',
        confirmButtonText: tr("Try Again"),
        confirmButtonColor: '#F27474'
      });
    } else {
      setLoading(true);
      const token = await loginUser({
        email,
        password,
        setLoading
      });

      if (token?.status_code >= 200 && token?.status_code <= 299) {
        // Stash credentials (base64) for THIS session so the Stations page can
        // build an auto-login (#sso=) link into a station's Automation UI for
        // station-bound users. Interim only — the proper fix is a short-lived
        // server-minted token instead of holding the password.
        try {
          sessionStorage.setItem('sso', btoa(unescape(encodeURIComponent(`${email}:${password}`))));
        } catch (e) {
          /* non-Latin creds — skip auto-login, manual still works */
        }
        setToken(token);
      } else if (token?.status_code === 401) {
        setLoading(false);
        setErrorState({ type: true, label: tr("Error"), helperText: tr("Check email and password.") });
      } else {
        setLoading(false);
      }
    }
  };

  const clearError = () => {
    if (errorState.type) {
      setErrorState({ type: false, label: tr("Password"), helperText: '' });
    }
  };

  return (
    <div className="ak-login">
      {/* ── Brand panel ─────────────────────────────────────── */}
      <aside className="ak-login__brand" aria-hidden="true">
        <div className="ak-login__brand-inner">
          <img src={logo} alt="" className="ak-login__logo" />
          <h2 className="ak-login__brand-title">{tr("Station Automation")}</h2>
          <p className="ak-login__brand-copy">
            {tr("Tanks, pumps and receipts across every station in one place.")}
          </p>

          <ul className="ak-login__legend">
            
            
            
            
          </ul>
        </div>
      </aside>

      {/* ── Form panel ──────────────────────────────────────── */}
      <main className="ak-login__panel">
        <form id="loginForm" className="ak-login__form" onSubmit={handleSubmit} noValidate>
          <h1 className="ak-login__title">{tr("Sign in")}</h1>
          <p className="ak-login__subtitle">{tr("Use your station account to continue.")}</p>

          <div className="ak-field">
            <label className="ak-field__label" htmlFor="ak-email">
              {tr("Email")}
            </label>
            <input
              id="ak-email"
              className={`ak-field__input${errorState.type ? ' is-invalid' : ''}`}
              type="email"
              autoComplete="username"
              autoFocus
              placeholder={tr("you@company.com")}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
            />
          </div>

          <div className="ak-field">
            <label className="ak-field__label" htmlFor="ak-password">
              {tr("Password")}
            </label>
            <div className="ak-field__wrap">
              <input
                id="ak-password"
                className={`ak-field__input${errorState.type ? ' is-invalid' : ''}`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                aria-describedby={errorState.helperText ? 'ak-password-error' : undefined}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError();
                }}
              />
              <button
                type="button"
                className="ak-field__reveal"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? tr("Hide password") : tr("Show password")}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} />
              </button>
            </div>
            {errorState.helperText ? (
              <p className="ak-field__error" id="ak-password-error" role="alert">
                {errorState.helperText}
              </p>
            ) : null}
          </div>

          <button
            className={`ak-button${loading ? ' is-loading' : ''}`}
            type="submit"
            form="loginForm"
            disabled={loading}
          >
            <span className="ak-button__label">{loading ? tr("Signing in") : tr("Sign in")}</span>
          </button>
        </form>
      </main>
    </div>
  );
}

Login.propTypes = {
  setToken: PropTypes.func.isRequired
};